import React, { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { firestore, storage } from './firebase/config';
import { ref, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';

const BusinessUserDashboard = () => {
  const { currentUser, isAuthenticated, isBusinessUser, logout } = useAuth();
  const navigate = useNavigate();
  const [businessProfile, setBusinessProfile] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, jobs, applicants, profile
  const [loading, setLoading] = useState(true);
  const [redirectRequired, setRedirectRequired] = useState(false);

  useEffect(() => {
    // Check authentication status only once when component mounts or when auth state changes
    if (!isAuthenticated) {
      setRedirectRequired(true);
      return;
    }
    
    if (!isBusinessUser) {
      setRedirectRequired(true);
      return;
    }
    
    // Reset redirect flag if user is properly authenticated
    setRedirectRequired(false);
  }, [isAuthenticated, isBusinessUser]);
  
  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        if (currentUser && isAuthenticated && isBusinessUser) {
          // Fetch business profile
          const businessProfileDoc = await getDoc(doc(firestore, 'userProfiles', currentUser.uid));
          if (businessProfileDoc.exists()) {
            const profileData = businessProfileDoc.data();
            setBusinessProfile(profileData);
            
            // Check if business has a profile image in Storage or as a direct URL
            if (profileData.profileImagePath) {
              // If the path already starts with http/https, use it directly
              if (profileData.profileImagePath.startsWith('http')) {
                setProfileImageUrl(profileData.profileImagePath);
              } else {
                try {
                  // Otherwise try to get image from Firebase Storage
                  // Use the actual path from the profile if available, otherwise try with UID
                  // Using userProfilePhoto directory instead of profile_images since it's allowed in the rules
                  const storagePath = profileData.profileImagePath.startsWith('userProfilePhoto/') 
                    ? profileData.profileImagePath 
                    : `userProfilePhoto/${currentUser.uid}`;
                  
                  const imageRef = ref(storage, storagePath);
                  const url = await getDownloadURL(imageRef);
                  setProfileImageUrl(url);
                } catch (imageError) {
                  console.error('Error fetching business profile image:', imageError);
                  // If there's a storage error, try to use the profileImagePath directly if it exists
                  if (profileData.profileImagePath && !profileData.profileImagePath.startsWith('profile_images/')) {
                    setProfileImageUrl(`/asset/${profileData.profileImagePath}`);
                  }
                }
              }
            }
          }
          
          // Fetch a sample of applicants (in a real app, this would be actual applicants for this business's jobs)
          const applicantsQuery = query(
            collection(firestore, 'userProfiles'),
            where('role', '==', 'regular_user'),
            limit(5)
          );
          
          const applicantsSnapshot = await getDocs(applicantsQuery);
          const applicantsData = [];
          applicantsSnapshot.forEach(doc => {
            applicantsData.push({
              id: doc.id,
              ...doc.data()
            });
          });
          
          setApplicants(applicantsData);
        }
      } catch (error) {
        console.error('Error fetching business data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (isAuthenticated && isBusinessUser) {
      fetchBusinessData();
    }
  }, [currentUser, isAuthenticated, isBusinessUser]);
  
  // Handle redirects based on auth state
  if (redirectRequired) {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    
    if (!isBusinessUser) {
      return <Navigate to="/dashboard/home" replace />;
    }
  }

  const handleLogout = async () => {
    const success = await logout();
    if (success) {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <nav className="bg-white shadow">
        <div className="container mx-auto px-4 py-4 max-w-6xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img src="/asset/Logo/hireeLogo.png" alt="Hiree Logo" className="h-8 mr-4" />
              <h1 className="text-xl font-bold text-gray-800">Business Dashboard</h1>
            </div>
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/')}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Home
              </button>
              <button 
                onClick={handleLogout}
                className="px-4 py-2 text-white bg-[#FF8C00] rounded-md hover:bg-[#FF8C00]/90"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Business Profile Summary */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div className="flex items-center mb-4 md:mb-0">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mr-4 overflow-hidden">
                {profileImageUrl ? (
                  <img 
                    src={profileImageUrl} 
                    alt="Business Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : businessProfile?.profileImagePath ? (
                  <img 
                    src={businessProfile.profileImagePath.startsWith('http') ? businessProfile.profileImagePath : `/asset/${businessProfile.profileImagePath}`} 
                    alt="Business Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl text-gray-400">{businessProfile?.businessName?.charAt(0) || currentUser?.email?.charAt(0)?.toUpperCase() || 'B'}</span>
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold">
                  {businessProfile?.businessName || 'Your Restaurant'}
                </h2>
                <p className="text-gray-600">{currentUser?.email}</p>
                <div className="mt-1 inline-block px-3 py-1 bg-blue-100 rounded-full text-xs text-blue-800 font-medium">
                  Restaurant Owner
                </div>
              </div>
            </div>
            <div>
              <button 
                onClick={() => setActiveTab('profile')}
                className="px-4 py-2 text-[#FF8C00] border border-[#FF8C00] rounded-md hover:bg-[#FF8C00] hover:text-white transition-colors"
              >
                Edit Profile
              </button>
            </div>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="flex border-b">
            <button
              className={`flex-1 py-4 px-6 text-center ${activeTab === 'dashboard' ? 'text-[#FF8C00] border-b-2 border-[#FF8C00] font-medium' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setActiveTab('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`flex-1 py-4 px-6 text-center ${activeTab === 'jobs' ? 'text-[#FF8C00] border-b-2 border-[#FF8C00] font-medium' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setActiveTab('jobs')}
            >
              Job Listings
            </button>
            <button
              className={`flex-1 py-4 px-6 text-center ${activeTab === 'applicants' ? 'text-[#FF8C00] border-b-2 border-[#FF8C00] font-medium' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setActiveTab('applicants')}
            >
              Applicants
            </button>
          </div>
          
          <div className="p-6">
            {/* Dashboard Content */}
            {activeTab === 'dashboard' && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white rounded-lg border p-6">
                    <h3 className="text-3xl font-bold text-[#FF8C00] mb-2">5</h3>
                    <p className="text-gray-600">Active Job Listings</p>
                  </div>
                  <div className="bg-white rounded-lg border p-6">
                    <h3 className="text-3xl font-bold text-[#FF8C00] mb-2">12</h3>
                    <p className="text-gray-600">New Applicants</p>
                  </div>
                  <div className="bg-white rounded-lg border p-6">
                    <h3 className="text-3xl font-bold text-[#FF8C00] mb-2">3</h3>
                    <p className="text-gray-600">Positions to Fill</p>
                  </div>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="divide-y">
                      <div className="p-4 hover:bg-gray-50">
                        <p className="text-gray-800">New application for <span className="font-medium">Head Chef</span> position</p>
                        <p className="text-sm text-gray-500 mt-1">2 hours ago</p>
                      </div>
                      <div className="p-4 hover:bg-gray-50">
                        <p className="text-gray-800">Your job posting for <span className="font-medium">Server</span> is expiring soon</p>
                        <p className="text-sm text-gray-500 mt-1">Yesterday</p>
                      </div>
                      <div className="p-4 hover:bg-gray-50">
                        <p className="text-gray-800">You received 3 new applications for <span className="font-medium">Bartender</span></p>
                        <p className="text-sm text-gray-500 mt-1">2 days ago</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Quick Actions</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => setActiveTab('jobs')}
                      className="p-4 border rounded-lg hover:bg-gray-50 text-left"
                    >
                      <h4 className="font-medium text-[#FF8C00] mb-2">Post a New Job</h4>
                      <p className="text-sm text-gray-600">Create a new job listing to find qualified staff for your restaurant</p>
                    </button>
                    <button
                      onClick={() => setActiveTab('applicants')}
                      className="p-4 border rounded-lg hover:bg-gray-50 text-left"
                    >
                      <h4 className="font-medium text-[#FF8C00] mb-2">Review Applicants</h4>
                      <p className="text-sm text-gray-600">Check applications and find the perfect match for your team</p>
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Job Listings Tab */}
            {activeTab === 'jobs' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold">Your Job Listings</h3>
                  <button className="px-4 py-2 bg-[#FF8C00] text-white rounded-md hover:bg-[#FF8C00]/90">
                    + Post New Job
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Sample job listings - would be fetched from Firestore in a real app */}
                  {[1, 2, 3, 4, 5].map((job) => (
                    <div key={job} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex flex-col md:flex-row md:items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-lg">
                            {job === 1 ? 'Head Chef' : job === 2 ? 'Server' : job === 3 ? 'Bartender' : job === 4 ? 'Host/Hostess' : 'Line Cook'}
                          </h4>
                          <p className="text-gray-600 text-sm mt-1">
                            {job === 1 ? 'Full-time' : job === 2 ? 'Part-time' : job === 3 ? 'Full-time' : job === 4 ? 'Part-time' : 'Full-time'} • 
                            ${job === 1 ? '25-30' : job === 2 ? '15-18' : job === 3 ? '18-22' : job === 4 ? '14-16' : '16-20'}/hour • 
                            Posted {job} day{job > 1 ? 's' : ''} ago
                          </p>
                        </div>
                        <div className="flex items-center mt-4 md:mt-0 space-x-3">
                          <div className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full">
                            {job === 1 ? '8' : job === 2 ? '12' : job === 3 ? '5' : job === 4 ? '3' : '7'} applicants
                          </div>
                          <button className="text-gray-500 hover:text-gray-800">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Applicants Tab */}
            {activeTab === 'applicants' && (
              <div>
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-2">Applicants</h3>
                  <p className="text-gray-600">Review and contact potential staff members for your restaurant</p>
                </div>
                
                <div className="flex flex-col sm:flex-row mb-6 gap-4">
                  <div className="flex-1">
                    <input 
                      type="text" 
                      placeholder="Search applicants" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                    />
                  </div>
                  <select className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]">
                    <option>All Positions</option>
                    <option>Head Chef</option>
                    <option>Server</option>
                    <option>Bartender</option>
                    <option>Host/Hostess</option>
                    <option>Line Cook</option>
                  </select>
                </div>
                
                <div className="space-y-6">
                  {applicants.length > 0 ? (
                    applicants.map((applicant, index) => (
                      <div key={applicant.id || index} className="border rounded-lg overflow-hidden">
                        <div className="p-4 md:p-6">
                          <div className="flex flex-col md:flex-row md:items-center">
                            <div className="flex items-center mb-4 md:mb-0 md:mr-6">
                              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mr-4 overflow-hidden">
                                {applicant.profileImagePath ? (
                                  <img 
                                    src={applicant.profileImagePath.startsWith('http') ? applicant.profileImagePath : `/asset/${applicant.profileImagePath}`} 
                                    alt="Applicant" 
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="text-2xl text-gray-400">
                                    {applicant.firstName?.charAt(0) || applicant.email?.charAt(0)?.toUpperCase() || 'A'}
                                  </span>
                                )}
                              </div>
                              <div>
                                <h4 className="font-semibold text-lg">
                                  {applicant.firstName} {applicant.lastName}
                                </h4>
                                <p className="text-gray-600">{applicant.email}</p>
                                {applicant.jobTitle && (
                                  <p className="text-sm text-gray-500 mt-1">{applicant.jobTitle}</p>
                                )}
                              </div>
                            </div>
                            
                            <div className="md:ml-auto flex flex-wrap gap-2">
                              {applicant.skills && applicant.skills.length > 0 ? (
                                applicant.skills.map((skill, idx) => (
                                  <span key={idx} className="px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-800">
                                    {skill}
                                  </span>
                                ))
                              ) : (
                                <span className="text-sm text-gray-500">No skills listed</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                              <div className="mb-4 sm:mb-0">
                                <div className="flex items-center">
                                  <svg className="w-5 h-5 text-[#FF8C00] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                  </svg>
                                  <span className="text-sm text-gray-600">
                                    Available: {applicant.workAvailability || 'Not specified'}
                                  </span>
                                </div>
                                {applicant.location && (
                                  <div className="flex items-center mt-2">
                                    <svg className="w-5 h-5 text-[#FF8C00] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                    </svg>
                                    <span className="text-sm text-gray-600">
                                      {applicant.location}
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex space-x-2">
                                <button className="px-4 py-2 text-[#FF8C00] border border-[#FF8C00] rounded-md hover:bg-[#FF8C00] hover:text-white transition-colors">
                                  View Profile
                                </button>
                                <button className="px-4 py-2 bg-[#FF8C00] text-white rounded-md hover:bg-[#FF8C00]/90">
                                  Contact
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-10">
                      <p className="text-gray-500">No applicants found</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessUserDashboard;
